import {Injectable} from '@angular/core';

const AES_ALGORITHM = 'AES-GCM';
const IV_LENGTH_BYTES = 12;
// Namespaces both the sessionStorage key and the stored value for entries this service manages,
// so clear()/remove() can recognize (and only ever touch) their own entries - eg. leaving an
// unrelated, unencrypted login token key untouched.
const PREFIX = 'enc:';

/**
 * Transparently encrypts/decrypts values written to sessionStorage using AES-GCM (Web Crypto API).
 * The AES key is generated once per tab, kept non-extractable and is never persisted anywhere -
 * it only ever exists in this service instance's memory and is gone once the tab is closed.
 * This does not protect against an attacker with an active debugger on the same machine, but it
 * does prevent sensitive data from being readable in plain text via the Storage/Application tab,
 * browser extensions with storage access, or accidental logging of sessionStorage contents.
 */
@Injectable({
  providedIn: 'root'
})
export class SecureSessionStorageService {

  private readonly cryptoKeyPromise: Promise<CryptoKey> = crypto?.subtle?.generateKey(
    { name: AES_ALGORITHM, length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  async set<T>(key: string, value: T): Promise<void> {
    const json = JSON.stringify(value);
    if (json === undefined) {
      throw new Error(`SecureSessionStorageService: value for key "${key}" is not JSON-serializable.`);
    }
    const ciphertext = await this.encrypt(json);
    sessionStorage.setItem(this.prefixedKey(key), PREFIX + ciphertext);
  }

  async get<T>(key: string): Promise<T | null> {
    const stored = this.readOwn(key);
    if (stored === null) {
      return null;
    }
    const plaintext = await this.decrypt(stored);
    return plaintext !== null ? JSON.parse(plaintext) as T : null;
  }

  remove(key: string): void {
    const storageKey = this.prefixedKey(key);
    const stored = sessionStorage.getItem(storageKey);
    if (stored === null) {
      return;
    }
    if (!stored.startsWith(PREFIX)) {
      throw new Error(`SecureSessionStorageService: refusing to remove "${key}" - entry was not created by this service.`);
    }
    sessionStorage.removeItem(storageKey);
  }

  /**
   * Only clears entries this service created (identified by the key prefix) - unrelated
   * sessionStorage entries (eg. an unencrypted login token) are left untouched.
   */
  clear(): void {
    for (const storageKey of Object.keys(sessionStorage)) {
      if (storageKey.startsWith(PREFIX)) {
        sessionStorage.removeItem(storageKey);
      }
    }
  }

  private prefixedKey(key: string): string {
    return PREFIX + key;
  }

  private readOwn(key: string): string | null {
    const stored = sessionStorage.getItem(this.prefixedKey(key));
    return stored?.startsWith(PREFIX) ? stored.slice(PREFIX.length) : null;
  }

  private async encrypt(plaintext: string): Promise<string> {
    const cryptoKey = await this.cryptoKeyPromise;
    const iv = crypto?.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
    const ciphertext = await crypto?.subtle?.encrypt({ name: AES_ALGORITHM, iv }, cryptoKey, new TextEncoder().encode(plaintext));
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(ciphertext), iv.length);
    return this.toBase64(combined);
  }

  private async decrypt(stored: string): Promise<string | null> {
    try {
      const cryptoKey = await this.cryptoKeyPromise;
      const combined = this.fromBase64(stored);
      const iv = combined.slice(0, IV_LENGTH_BYTES);
      const ciphertext = combined.slice(IV_LENGTH_BYTES);
      const decrypted = await crypto?.subtle?.decrypt({ name: AES_ALGORITHM, iv }, cryptoKey, ciphertext);
      return new TextDecoder().decode(decrypted);
    } catch {
      // foreign/corrupted ciphertext (e.g. written by a different key/session) cannot be decrypted
      return null;
    }
  }

  private toBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach(byte => binary += String.fromCharCode(byte));
    return btoa(binary);
  }

  private fromBase64(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

}
